import { _ } from "libs";

import React, { useRef, useLayoutEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroller";
import Loader from "../loader";
import DirectConversationItem from "../items/conversation/direct";
import GroupConversationItem from "../items/conversation/group";
import { MessagePartner } from "../../types";
import cx from "classnames";
import { relativePastTime } from "../../actions/relativePastTime";
import { ChatConversationListData } from "../../libs/conversationsMap";
import withRedux from "../../withRedux";
import { ChatState } from "../../reducers/chat";
import { selectConversation, loadPreviousConversations } from "../../actions/chat";
import { formatUserName } from "../../libs/formatName";
import { Subject } from "rxjs";
import { filterPartners } from "./functions";

const getConversationPartnerNames = (partners: MessagePartner[]): string => {
	if (partners.length === 0) {
		return _("chatBot.fullName");
	}
	if (partners.length === 1) {
		return formatUserName(partners[0]);
	}
	return partners.map(({ firstName }) => firstName).join(", ");
};

const getConversationPartnerAvatar = (partners: MessagePartner[]): string[] => {
	if (partners.length === 0) {
		return ["/images/avatar_gotcourts_bot.svg"];
	}
	if (partners.length === 1) {
		return [partners[0].photoUrl];
	}
	if (partners.length >= 2) {
		return [partners[0].photoUrl, partners[1].photoUrl];
	}
};

const getFirstName = (player: MessagePartner): string => {
	if (!player) {
		return "FIRST NAME ERROR";
	}
	if (player.firstName) {
		return player.firstName;
	}
	return "FIRST NAME ERROR";
};

type ConversationItem = typeof GroupConversationItem | typeof DirectConversationItem;

type ListItem = {
	children: ConversationItem;
};

const createListItem = ({
	conversation,
	userId,
	activeConversationId,
	onClick,
}: {
	conversation: ChatConversationListData;
	userId: string;
	activeConversationId: string;
	onClick: (conversationId: string) => void;
}) => {
	const partners = filterPartners(conversation.partners, userId);
	const isGroupConversation = partners.length > 1;
	const conversationName = getConversationPartnerNames(partners);
	const avatarPhotoUrl = getConversationPartnerAvatar(partners);
	const messagePreview = conversation.messagePreview;
	let lastSender = null as string;
	let relativeTime = relativePastTime(conversation.lastUpdate);

	if (isGroupConversation) {
		if (conversation.lastSenderId === userId) {
			lastSender = _("me");
		} else {
			lastSender = getFirstName(conversation.partnersHash[conversation.lastSenderId]);
		}
	}

	const className = cx("cell clickable", {
		selected: activeConversationId && conversation.conversationId === activeConversationId,
	});

	return (
		<div
			className={className}
			key={conversation.conversationId}
			onClick={onClick.bind(null, conversation.conversationId)}
		>
			{isGroupConversation ? (
				<GroupConversationItem
					title={conversationName}
					preview={messagePreview}
					photoUrls={avatarPhotoUrl}
					unreadCount={conversation.numberOfUnreadMessages}
					membersCount={conversation.partners.length}
					lastSender={lastSender}
					lastUpdate={relativeTime}
					key={conversation.conversationId}
					conversationId={conversation.conversationId}
				/>
			) : (
				<DirectConversationItem
					name={conversationName}
					preview={messagePreview}
					lastUpdate={relativeTime}
					photoUrl={avatarPhotoUrl[0]}
					unreadCount={conversation.numberOfUnreadMessages}
					key={conversation.conversationId}
					conversationId={conversation.conversationId}
				/>
			)}
		</div>
	);
};

const createConversationsList = (
	conversations: ChatConversationListData[],
	activeConversationId: string,
	userId: string,
	onClick: (conversationId: string) => void
): JSX.Element[] | ListItem[] => {
	return conversations.map((conversation, key: number) => {
		return createListItem({ conversation, userId, activeConversationId, onClick });
	});
};

type Props = {
	activeConversationId: string;
	conversations: ChatConversationListData[];
	selectConversation: typeof selectConversation;
	onConversationSelect: (conversationId: string) => void;
	hasMoreConversations: boolean;
	oldestMessageTimestamp: number;
	loadPreviousConversations: typeof loadPreviousConversations;
	userId: string;
	lastUpdate: number;
};

const ConversationsList = ({
	activeConversationId,
	conversations,
	selectConversation,
	onConversationSelect,
	hasMoreConversations,
	oldestMessageTimestamp,
	loadPreviousConversations,
	userId,
	lastUpdate,
}: Props) => {
	const [click$, setClickStream] = useState(null as Subject<string>);

	useLayoutEffect(() => {
		const click$ = new Subject<string>();

		const subscription = click$.subscribe(id => {
			selectConversation(id);
		});

		setClickStream(click$);

		return () => subscription.unsubscribe;
	}, []);

	const node = useRef<HTMLDivElement>();

	const loadMore = () => {
		loadPreviousConversations(oldestMessageTimestamp);
	};

	const handleConversationSelect = (id: string) => {
		onConversationSelect(id);
		click$.next(id);
	};

	const list = createConversationsList(conversations, activeConversationId, userId, handleConversationSelect);

	return (
		<div className="table conversations" ref={node}>
			<InfiniteScroll
				pageStart={0}
				loadMore={loadMore}
				hasMore={hasMoreConversations}
				loader={<Loader key="loader" />}
				useWindow={false}
			>
				{list}
			</InfiniteScroll>
		</div>
	);
};

const mapStateToProps = ({
	chat: { conversations, activeConversationId, oldestMessageTimestamp, hasMoreConversations, lastUpdate },
}: {
	chat: ChatState;
}) => {
	return {
		conversations: conversations.getListData(),
		activeConversationId,
		oldestMessageTimestamp,
		hasMoreConversations,
		lastUpdate,
	};
};

export default withRedux(
	{
		selectConversation,
		loadPreviousConversations,
	},
	mapStateToProps
)(ConversationsList);
